import './coapplicantPreviousResidencePage.scss';

import { ThemeProvider } from '@mui/material/styles';
import isEmpty from 'lodash/isEmpty';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AddressLineOne } from '../../../components/AddressLineOne/AddressLineOne';
import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { Input } from '../../../components/Input/Input';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { NumberInput } from '../../../components/NumberInput/NumberInput';
import { Select } from '../../../components/Select/Select';
import { USStates } from '../../../store/addressSuggestionStore/constants/states';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();

  const {
    coapplicantPreviousResidenceCity,
    coapplicantPreviousResidenceLineOne,
    coapplicantPreviousResidenceLineTwo,
    coapplicantPreviousResidencePostalCode,
    coapplicantPreviousResidenceState,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantPreviousResidenceCity,
    setCoapplicantPreviousResidenceLineOne,
    setCoapplicantPreviousResidenceLineTwo,
    setCoapplicantPreviousResidencePostalCode,
    setCoapplicantPreviousResidenceState,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    formState: { errors, isDirty, isValid },
    handleSubmit,
    register,
    setValue,
  } = useForm({
    criteriaMode: 'firstError',
    defaultValues: {
      cityValue: coapplicantPreviousResidenceCity,
      lineOneValue: coapplicantPreviousResidenceLineOne,
      lineTwoValue: coapplicantPreviousResidenceLineTwo,
      postalCodeValue: coapplicantPreviousResidencePostalCode,
      stateValue: coapplicantPreviousResidenceState,
    },
    delayError: 200,
    mode: 'onTouched',
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const addressValidation = {
    required: t('validation.address.required'),
  };

  const cityValidation = {
    required: t('validation.city.required'),
  };

  const stateValidation = {
    required: t('validation.state.required'),
  };

  const postalCodeValidation = {
    required: t('validation.zipCode.required'),
    minLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
    maxLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      cityValue,
      lineOneValue,
      lineTwoValue,
      stateValue,
      postalCodeValue,
    } = values;

    if (!cityValue || !lineOneValue || !stateValue || !postalCodeValue) {
      return;
    }

    setCoapplicantPreviousResidenceCity(cityValue);
    setCoapplicantPreviousResidenceLineOne(lineOneValue);
    setCoapplicantPreviousResidenceLineTwo(lineTwoValue);
    setCoapplicantPreviousResidencePostalCode(postalCodeValue);
    setCoapplicantPreviousResidenceState(stateValue);

    if (isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="page coapplicant-previous-residence-page">
        <Header
          progress={useWorkflowStore.getState().computed.progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.coapplicantPreviousResidence.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={locator.pages.coapplicantPreviousResidence.form.h1}
              >
                {t('CoapplicantPreviousResidencePage.title')}
              </h1>
              <h2
                data-testid={locator.pages.coapplicantPreviousResidence.form.h2}
              >
                {t('CoapplicantPreviousResidencePage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantPreviousResidence.alert.error
                    }
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div style={{ marginTop: '1.5rem' }}>
                  <AddressLineOne
                    data-testid={
                      locator.pages.coapplicantPreviousResidence.form
                        .addressLine1Input
                    }
                    errorTestId={
                      locator.pages.coapplicantPreviousResidence.form
                        .addressLine1InputError
                    }
                    errors={errors}
                    label={t(
                      'CoapplicantPreviousResidencePage.residenceLineOne',
                    )}
                    name="lineOneValue"
                    register={register}
                    setValue={setValue}
                    suggestionFields={{
                      cityFieldName: 'cityValue',
                      stateFieldName: 'stateValue',
                      postalCodeFieldName: 'postalCodeValue',
                    }}
                    validation={addressValidation}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <Input
                        {...register('lineTwoValue')}
                        autoComplete="address-line2"
                        data-testid={
                          locator.pages.coapplicantPreviousResidence.form
                            .addressLine2Input
                        }
                        label={t(
                          'CoapplicantPreviousResidencePage.residenceLineTwo',
                        )}
                      />
                    </div>

                    <div className="input-half">
                      <Input
                        {...register('cityValue', cityValidation)}
                        autoComplete="address-level2"
                        data-testid={
                          locator.pages.coapplicantPreviousResidence.form
                            .cityInput
                        }
                        error={errors.cityValue}
                        errorTestId={
                          locator.pages.coapplicantPreviousResidence.form
                            .cityInputError
                        }
                        label={t(
                          'CoapplicantPreviousResidencePage.residenceCity',
                        )}
                      />
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <Select
                        {...register('stateValue', stateValidation)}
                        error={errors.stateValue}
                        errorTestId={
                          locator.pages.coapplicantPreviousResidence.form
                            .stateSelectError
                        }
                        inputTestId={
                          locator.pages.coapplicantPreviousResidence.form
                            .stateSelect
                        }
                        label={t(
                          'CoapplicantPreviousResidencePage.residenceState',
                        )}
                        options={USStates.map(({ abbreviation, name }) => ({
                          name,
                          value: abbreviation,
                        }))}
                      />
                    </div>

                    <div className="input-half">
                      <NumberInput
                        {...register('postalCodeValue', postalCodeValidation)}
                        autoComplete="postal-code"
                        data-testid={
                          locator.pages.coapplicantPreviousResidence.form
                            .zipCodeInput
                        }
                        error={errors.postalCodeValue}
                        errorTestId={
                          locator.pages.coapplicantPreviousResidence.form
                            .zipCodeInputError
                        }
                        label={t('CoapplicantPreviousResidencePage.zip')}
                        type="number"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  data-testid={
                    locator.pages.coapplicantPreviousResidence.form.submitButton
                  }
                  disabled={!isValid}
                  loader={isSyncingCoapplicant}
                  fullWidth
                  submit
                >
                  {t('CoapplicantPreviousResidencePage.continue')}
                </Button>
              </div>
            </div>
            <footer
              data-testid={
                locator.pages.coapplicantPreviousResidence.footer.root
              }
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantPreviousResidencePage';
